import { useEffect, useState } from 'react';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import {
  fetchSiteSettings,
  updateSiteSettings,
  type DuitNowQrMode,
} from '@/api/siteSettings';
import DefaultDuitNowQrUpload from './DefaultDuitNowQrUpload';
import TestMobileNumbersCard from './TestMobileNumbersCard';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

type TrackerUrlCardProps = {
  title: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
  saving: boolean;
  error: string;
  success: string;
  disabled: boolean;
};

function TrackerUrlCard({
  title,
  value,
  onChange,
  onSave,
  saving,
  error,
  success,
  disabled,
}: TrackerUrlCardProps) {
  return (
    <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      <form
        className="mt-4 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave();
        }}
      >
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            URL
          </label>
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
            placeholder="https://"
            disabled={disabled || saving}
          />
        </div>

        {error ? (
          <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
            {success}
          </div>
        ) : null}

        <Button type="submit" variant="primary" disabled={disabled || saving}>
          {saving ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </form>
    </div>
  );
}

function Main() {
  const [qrMode, setQrMode] = useState<DuitNowQrMode>('individual');
  const [hasDefaultQr, setHasDefaultQr] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState('');
  const [savingMode, setSavingMode] = useState(false);
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [stockTrackerUrl, setStockTrackerUrl] = useState('');
  const [savingPaymentUrl, setSavingPaymentUrl] = useState(false);
  const [savingStockUrl, setSavingStockUrl] = useState(false);
  const [paymentUrlError, setPaymentUrlError] = useState('');
  const [stockUrlError, setStockUrlError] = useState('');
  const [paymentUrlSuccess, setPaymentUrlSuccess] = useState('');
  const [stockUrlSuccess, setStockUrlSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSettingsLoading(true);
    setSettingsError('');
    void fetchSiteSettings()
      .then((settings) => {
        if (cancelled) return;
        setQrMode(settings.duitNowQrMode);
        setHasDefaultQr(settings.hasDefaultDuitNowQr);
        setPaymentProofUrl(settings.paymentProofUrl);
        setStockTrackerUrl(settings.stockTrackerUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setSettingsError(err instanceof Error ? err.message : 'Failed to load site settings');
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleQrModeChange = async (nextMode: DuitNowQrMode) => {
    if (nextMode === qrMode || savingMode || settingsLoading) return;

    const previous = qrMode;
    setQrMode(nextMode);
    setSavingMode(true);
    setSettingsError('');
    try {
      const settings = await updateSiteSettings({ duitNowQrMode: nextMode });
      setQrMode(settings.duitNowQrMode);
      setHasDefaultQr(settings.hasDefaultDuitNowQr);
      setPaymentProofUrl(settings.paymentProofUrl);
      setStockTrackerUrl(settings.stockTrackerUrl);
    } catch (err) {
      setQrMode(previous);
      setSettingsError(err instanceof Error ? err.message : 'Failed to update QR mode');
    } finally {
      setSavingMode(false);
    }
  };

  const handleSavePaymentUrl = async () => {
    setPaymentUrlError('');
    setPaymentUrlSuccess('');
    if (!paymentProofUrl.trim()) {
      setPaymentUrlError('URL is required');
      return;
    }
    setSavingPaymentUrl(true);
    try {
      const settings = await updateSiteSettings({ paymentProofUrl });
      setPaymentProofUrl(settings.paymentProofUrl);
      setPaymentUrlSuccess('Payment tracker URL saved');
    } catch (err) {
      setPaymentUrlError(err instanceof Error ? err.message : 'Failed to save URL');
    } finally {
      setSavingPaymentUrl(false);
    }
  };

  const handleSaveStockUrl = async () => {
    setStockUrlError('');
    setStockUrlSuccess('');
    if (!stockTrackerUrl.trim()) {
      setStockUrlError('URL is required');
      return;
    }
    setSavingStockUrl(true);
    try {
      const settings = await updateSiteSettings({ stockTrackerUrl });
      setStockTrackerUrl(settings.stockTrackerUrl);
      setStockUrlSuccess('Stock tracker URL saved');
    } catch (err) {
      setStockUrlError(err instanceof Error ? err.message : 'Failed to save URL');
    } finally {
      setSavingStockUrl(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Payment QR Code Settings
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Choose whether staff payment uses one default DuitNow QR or each ambassador&apos;s
          own upload.
        </p>

        {settingsError ? (
          <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
            {settingsError}
          </div>
        ) : null}

        {settingsLoading ? (
          <div className="mt-5 flex items-center gap-2 text-sm text-slate-400">
            <Lucide icon="Loader" className="w-4 h-4 animate-spin" />
            Loading settings…
          </div>
        ) : (
          <div
            role="radiogroup"
            aria-label="DuitNow QR Code mode"
            className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <button
              type="button"
              role="radio"
              aria-checked={qrMode === 'default'}
              disabled={savingMode}
              onClick={() => void handleQrModeChange('default')}
              className={`text-left text-sm font-medium transition ${
                qrMode === 'default'
                  ? 'text-slate-800 dark:text-slate-100'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            >
              Use Default DuitNow QR Code
            </button>

            <button
              type="button"
              role="switch"
              aria-checked={qrMode === 'individual'}
              aria-label="Use Individual DuitNow QR Code"
              disabled={savingMode}
              onClick={() =>
                void handleQrModeChange(qrMode === 'default' ? 'individual' : 'default')
              }
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                qrMode === 'individual' ? 'bg-primary' : 'bg-slate-300 dark:bg-darkmode-400'
              } ${savingMode ? 'cursor-wait opacity-70' : ''}`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-all ${
                  qrMode === 'individual' ? 'left-5' : 'left-0.5'
                }`}
              />
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={qrMode === 'individual'}
              disabled={savingMode}
              onClick={() => void handleQrModeChange('individual')}
              className={`text-left text-sm font-medium transition ${
                qrMode === 'individual'
                  ? 'text-slate-800 dark:text-slate-100'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            >
              Individual DuitNow QR Code
            </button>
          </div>
        )}
      </div>

      {qrMode === 'default' && !settingsLoading ? (
        <DefaultDuitNowQrUpload
          hasDefaultDuitNowQr={hasDefaultQr}
          onHasImageChange={setHasDefaultQr}
        />
      ) : null}

      <TrackerUrlCard
        title="Payment Tracker"
        value={paymentProofUrl}
        onChange={(next) => {
          setPaymentProofUrl(next);
          setPaymentUrlSuccess('');
        }}
        onSave={handleSavePaymentUrl}
        saving={savingPaymentUrl}
        error={paymentUrlError}
        success={paymentUrlSuccess}
        disabled={settingsLoading}
      />

      <TrackerUrlCard
        title="Stock Tracker"
        value={stockTrackerUrl}
        onChange={(next) => {
          setStockTrackerUrl(next);
          setStockUrlSuccess('');
        }}
        onSave={handleSaveStockUrl}
        saving={savingStockUrl}
        error={stockUrlError}
        success={stockUrlSuccess}
        disabled={settingsLoading}
      />

      <TestMobileNumbersCard />
    </div>
  );
}

export default Main;
